// client/src/coms/components/ReportDialog.js
// Report something to the server's moderators: a message (message prop), or (target prop)
// a member, an uploaded file, the server's own content or an activity:
//   target: { type: 'user' | 'file' | 'server' | 'activity', serverKey, userId, uploadId, fileId, ref, label }
// The server rate-limits reports and keeps them private to reviewers.
import React, { useEffect, useRef, useState } from 'react';
import { comsApiPost } from '../comsApi';

const REASONS = [
  ['spam', 'Spam or scam'],
  ['harassment', 'Harassment or bullying'],
  ['hate', 'Hate speech'],
  ['sexual', 'Sexual content'],
  ['violence', 'Violence or threats'],
  ['self_harm', 'Self-harm'],
  ['illegal', 'Illegal content'],
  ['other', 'Something else'],
];

const TITLES = { message: 'Report message', user: 'Report member', file: 'Report file', server: 'Report server content', activity: 'Report activity' };

export default function ReportDialog({ message, target, onClose }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const boxRef = useRef(null);
  const type = message ? 'message' : (target && target.type) || '';

  useEffect(() => {
    if (!type) return undefined;
    const prev = document.activeElement;
    try {
      if (boxRef.current) boxRef.current.focus();
    } catch {}
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      try {
        if (prev && prev.focus) prev.focus();
      } catch {}
    };
  }, [type, onClose]);

  if (!type) return null;

  async function submit() {
    setBusy(true);
    setErr('');
    try {
      const body =
        type === 'message'
          ? { message_id: Number(message.id), reason, details }
          : {
              target_type: type,
              server_key: target.serverKey,
              user_id: target.userId ? Number(target.userId) : undefined,
              upload_id: target.uploadId ? Number(target.uploadId) : undefined,
              file_id: target.fileId ? Number(target.fileId) : undefined,
              target_ref: target.ref || undefined,
              reason,
              details,
            };
      await comsApiPost('/reports', body);
      setDone(true);
    } catch (e) {
      setErr(String((e && e.data && e.data.error) || 'Could not send the report.'));
    } finally {
      setBusy(false);
    }
  }

  const preview = type === 'message' ? String(message.text || message.body || '').slice(0, 300) : String((target && target.label) || '').slice(0, 300);

  return (
    <div role="dialog" aria-modal="true" aria-label={TITLES[type]} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 16 }} onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div ref={boxRef} tabIndex={-1} style={{ width: 'min(420px, 100%)', background: 'var(--vxs-3, #1b1c1f)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 14, padding: 18, color: 'var(--vx-text, #fff)', outline: 'none' }}>
        <div style={{ fontSize: 17, fontWeight: 900 }}>{TITLES[type]}</div>
        {done ? (
          <>
            <div style={{ marginTop: 10, fontSize: 13 }}>Thanks. The server's moderators will review it. Reports are private: the person you reported is not told who sent it.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" className="coms-mini-btn primary" onClick={onClose}>
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            {preview ? (
              <div style={{ marginTop: 8, padding: 8, borderRadius: 8, background: 'rgba(var(--vx-fg-rgb, 255,255,255), calc(0.05 * var(--vx-ov, 1)))', fontSize: 12, maxHeight: 80, overflow: 'hidden' }}>
                {preview}
              </div>
            ) : null}
            <div role="radiogroup" aria-label="Reason" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
              {REASONS.map(([key, label]) => (
                <label key={key} style={{ display: 'flex', gap: 8, fontSize: 13 }}>
                  <input type="radio" name="report-reason" checked={reason === key} onChange={() => setReason(key)} />
                  {label}
                </label>
              ))}
            </div>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder="Anything moderators should know (optional)" aria-label="Details" maxLength={1000} rows={3} style={{ width: '100%', marginTop: 10, boxSizing: 'border-box', background: 'var(--vxs-1, #111214)', color: 'var(--vx-text, #fff)', border: '1px solid rgba(var(--vx-fg-rgb, 255,255,255), calc(0.12 * var(--vx-ov, 1)))', borderRadius: 8, padding: 8 }} />
            {err ? <div className="coms-form-error" style={{ marginTop: 8 }}>{err}</div> : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button type="button" className="coms-mini-btn" onClick={onClose} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="coms-mini-btn primary" onClick={submit} disabled={!reason || busy}>
                {busy ? 'Sending…' : 'Send report'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
